import { useEffect, useState } from 'react';
import { PADDLE_CLIENT_TOKEN } from '@/lib/kinde';

/**
 * Where the customer pays.
 *
 * The billing provider has NO hosted checkout page. Creating a transaction server-side returns
 * a link to one of OUR pages with `?_ptxn=<transaction id>` appended, and that page is what
 * actually opens the checkout — by loading the provider's script, which reads the parameter
 * and opens automatically. This route exists to be that page and nothing else.
 *
 * ## Why the script is loaded here and not in index.html
 *
 * It is needed on exactly one route. Loading it globally would put a third-party script on
 * every page of the product — including the corpus surfaces, which are the ones the
 * performance budget is written for — to serve a page most sessions never visit.
 *
 * ## What this page can and cannot do
 *
 * It cannot choose a price, name an account, or grant anything. All of that was settled
 * server-side when the transaction was created: the price came from `price_map` and the
 * account from a verified token. This page holds a transaction ID, which is a reference to
 * decisions already made — the worst a tampered `_ptxn` achieves is opening someone else's
 * checkout, which charges the tamperer's card and credits the other account.
 *
 * Entitlement still arrives only by verified webhook. Reaching the success state here proves
 * the browser saw a confirmation, which is not the same as the payment having settled.
 */
declare global {
  interface Window {
    Paddle?: {
      Environment: { set: (env: string) => void };
      Initialize: (opts: { token: string; eventCallback?: (e: PaddleEvent) => void }) => void;
    };
  }
}

interface PaddleEvent {
  name?: string;
}

const SCRIPT_SRC = 'https://cdn.paddle.com/paddle/v2/paddle.js';

type State = 'loading' | 'open' | 'done' | 'error';

export function CheckoutRoute() {
  const [state, setState] = useState<State>('loading');

  useEffect(() => {
    const txn = new URLSearchParams(window.location.search).get('_ptxn');
    if (!txn) {
      setState('error');
      return;
    }
    if (!PADDLE_CLIENT_TOKEN) {
      // A missing token is a deployment problem, not a customer problem. Say so in the console
      // and show the customer something honest rather than an overlay that never appears.
      console.error('PADDLE_CLIENT_TOKEN is not configured; checkout cannot open');
      setState('error');
      return;
    }

    const initialize = () => {
      try {
        window.Paddle?.Initialize({
          token: PADDLE_CLIENT_TOKEN,
          eventCallback: (event) => {
            if (event.name === 'checkout.completed') setState('done');
          },
        });
        // No open() call: the script finds `_ptxn` in the URL and opens the checkout itself.
        setState('open');
      } catch {
        setState('error');
      }
    };

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      initialize();
      return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = initialize;
    script.onerror = () => setState('error');
    document.head.appendChild(script);
  }, []);

  if (state === 'done') {
    return (
      <div className="checkout-status" role="status">
        <h1>Payment received</h1>
        {/* Deliberately not "your account is ready". The subscription becomes real when the
            provider's webhook is verified, which is usually immediate but is not guaranteed to
            have happened by the time this renders. */}
        <p>We’re activating your plan now — this usually takes a few seconds.</p>
        <p>
          <a href="/feed">Continue to Aware Insights</a>
        </p>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="checkout-status" role="alert">
        <h1>We couldn’t open checkout</h1>
        <p>Nothing has been charged. Please try choosing your plan again.</p>
        <p>
          <a href="/plan-pricing">Back to plans</a>
        </p>
      </div>
    );
  }

  return (
    <div className="checkout-status" role="status" aria-live="polite">
      <p>Opening secure checkout…</p>
    </div>
  );
}
